import { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Users, AlertTriangle, RefreshCw, Calendar, Layers, CalendarDays, Table2 } from 'lucide-react';
import { apiService, isWriteEnabled, queryKeys } from '@/services/api';
import { SortOption, type TableFilters, type DivisionPlayer } from '@/types/api';
import { toast } from '@/components/ui/Toaster';
import { saveRecentDivision } from '@/pages/Dashboard';
import PlayerTable from '@/components/PlayerTable';
import ControlPanel from '@/components/ControlPanel';
import ScheduleView from '@/components/ScheduleView';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { cleanDivisionName, type GameTabKey } from '@/utils/divisionGameTabs';

type ViewMode = 'players' | 'schedule';

export default function SeasonPage() {
  const { divisionId } = useParams<{ divisionId: string }>();
  const [searchParams] = useSearchParams();
  const showMatchup = searchParams.has('m');
  const [activeGameTab, setActiveGameTab] = useState<GameTabKey>('8ball');
  const [viewMode, setViewMode] = useState<ViewMode>('players');
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const canRefresh = isWriteEnabled;

  const [filters, setFilters] = useState<TableFilters>({
    search: '',
    gameType: 'all',
    sortBy: SortOption.EIGHTBALL_CSR,
    sortDirection: 'desc',
    groupByTeam: false,
  });

  // Track in recents
  useEffect(() => {
    if (divisionId) saveRecentDivision(divisionId);
  }, [divisionId]);

  // Update default sort when game tab changes
  useEffect(() => {
    const sortMap: Record<string, SortOption> = {
      '8ball': SortOption.EIGHTBALL_CSR,
      '9ball': SortOption.NINEBALL_CSR,
      '10ball': SortOption.TENBALL_CSR,
    };
    setFilters(f => ({ ...f, sortBy: sortMap[activeGameTab] || SortOption.EIGHTBALL_CSR }));
  }, [activeGameTab]);

  // Fetch merged season rankings
  const {
    data: rankings,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.seasonRankings(divisionId!, filters.groupByTeam),
    queryFn: () => apiService.getSeasonRankings(divisionId!, filters.groupByTeam),
    enabled: !!divisionId,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.message?.includes('No cached rankings')) return false;
      return failureCount < 1;
    },
  });

  // Fetch season metadata
  const { data: seasonInfo } = useQuery({
    queryKey: queryKeys.seasonInfo(divisionId!),
    queryFn: () => apiService.getSeasonInfo(divisionId!),
    enabled: !!divisionId,
    staleTime: 5 * 60 * 1000,
  });

  // Determine available game tabs from the actual data
  const availableGameTabs = useMemo<GameTabKey[]>(() => {
    if (!rankings?.metadata?.game_types?.length) return ['8ball'];
    const tabMap: Record<string, GameTabKey> = {
      '8ball': '8ball',
      '9ball': '9ball',
      '10ball': '10ball',
    };
    return rankings.metadata.game_types
      .map(gt => tabMap[gt])
      .filter((t): t is GameTabKey => !!t);
  }, [rankings?.metadata?.game_types]);

  useEffect(() => {
    if (!availableGameTabs.includes(activeGameTab)) {
      setActiveGameTab(availableGameTabs[0]);
    }
  }, [activeGameTab, availableGameTabs]);

  // Refresh: refresh both divisions in the pair
  const refreshMutation = useMutation({
    mutationFn: async () => {
      const ids = seasonInfo?.division_ids ?? [divisionId!];
      await Promise.all(ids.map(id => apiService.refreshDivision(id, { force: true })));
    },
    onMutate: () => setIsRefreshing(true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.seasonRankings(divisionId!, filters.groupByTeam) });
      toast.success('Season rankings refreshed.');
    },
    onError: (err: Error) => {
      toast.error(`Refresh failed: ${err.message}`);
    },
    onSettled: () => setIsRefreshing(false),
  });

  // Determine if this is a "current" season (last match within 45 days)
  const isDivisionCurrent = useMemo(() => {
    if (!seasonInfo?.last_match_date) return false;
    const lastMatch = new Date(seasonInfo.last_match_date);
    const daysSinceLastMatch = (Date.now() - lastMatch.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceLastMatch <= 45;
  }, [seasonInfo]);

  // Fetch period CSRs only for historical seasons
  const { data: periodStats } = useQuery({
    queryKey: queryKeys.divisionPeriodStats(divisionId!),
    queryFn: () => apiService.getDivisionPeriodStats(divisionId!),
    staleTime: 5 * 60 * 1000,
    enabled: !!divisionId && !isDivisionCurrent,
  });

  const getSortValue = (player: DivisionPlayer, sortBy: SortOption): any => {
    switch (sortBy) {
      case SortOption.NAME: return player.name.toLowerCase();
      case SortOption.TEAM: return (player.team_name || '').toLowerCase();
      case SortOption.EIGHTBALL_CSR: return player.games['8ball']?.skill_level || 0;
      case SortOption.NINEBALL_CSR: return player.games['9ball']?.skill_level || 0;
      case SortOption.TENBALL_CSR: return player.games['10ball']?.skill_level || 0;
      case SortOption.EIGHTBALL_CLASS: return (player.games['8ball']?.skill_class || 'ZZZ').toLowerCase();
      case SortOption.NINEBALL_CLASS: return (player.games['9ball']?.skill_class || 'ZZZ').toLowerCase();
      case SortOption.TENBALL_CLASS: return (player.games['10ball']?.skill_class || 'ZZZ').toLowerCase();
      case SortOption.EIGHTBALL_RANK: return player.games['8ball']?.local_rank ? parseInt(String(player.games['8ball'].local_rank)) : 999999;
      case SortOption.NINEBALL_RANK: return player.games['9ball']?.local_rank ? parseInt(String(player.games['9ball'].local_rank)) : 999999;
      case SortOption.TENBALL_RANK: return player.games['10ball']?.local_rank ? parseInt(String(player.games['10ball'].local_rank)) : 999999;
      case SortOption.EIGHTBALL_NATIONAL: return player.games['8ball']?.national_rank ? parseInt(String(player.games['8ball'].national_rank)) : 999999;
      case SortOption.NINEBALL_NATIONAL: return player.games['9ball']?.national_rank ? parseInt(String(player.games['9ball'].national_rank)) : 999999;
      case SortOption.TENBALL_NATIONAL: return player.games['10ball']?.national_rank ? parseInt(String(player.games['10ball'].national_rank)) : 999999;
      case SortOption.MATCHES_8BALL: return player.games['8ball']?.matches_played || 0;
      case SortOption.MATCHES_9BALL: return player.games['9ball']?.matches_played || 0;
      case SortOption.MATCHES_10BALL: return player.games['10ball']?.matches_played || 0;
      default: return player.name.toLowerCase();
    }
  };

  const filteredPlayers = useMemo(() => {
    if (!rankings?.players) return [];
    let filtered = rankings.players.filter((player) => {
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!player.name.toLowerCase().includes(s) && !player.team_name?.toLowerCase().includes(s) && !player.player_id.includes(s)) return false;
      }
      return true;
    });
    filtered.sort((a, b) => {
      const aVal = getSortValue(a, filters.sortBy);
      const bVal = getSortValue(b, filters.sortBy);
      const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : (aVal || 0) - (bVal || 0);
      return filters.sortDirection === 'desc' ? -cmp : cmp;
    });
    return filtered;
  }, [rankings?.players, filters.search, filters.sortBy, filters.sortDirection]);

  // Build a clean season name from the division names
  const seasonName = useMemo(() => {
    if (!seasonInfo?.divisions?.length) return `Season ${divisionId}`;
    const names = seasonInfo.divisions
      .map(d => cleanDivisionName(d.name))
      .filter(Boolean);
    // Prefer the 8-ball clean name, e.g. "Legend's 8-Ball"
    const eightBallName = names.find(n => n?.toLowerCase().includes('8-ball'));
    if (eightBallName) {
      // Strip the game type suffix — this is a season, not a single game division
      return eightBallName.replace(/\s*8-Ball$/i, '').trim() || eightBallName;
    }
    return names[0] || `Season ${divisionId}`;
  }, [seasonInfo, divisionId]);

  const dateRange = useMemo(() => {
    if (!seasonInfo?.first_match_date || !seasonInfo?.last_match_date) return '';
    const fmt = (s: string) => new Date(s.includes('T') ? s : s + 'T12:00:00')
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(seasonInfo.first_match_date)} – ${fmt(seasonInfo.last_match_date)}`;
  }, [seasonInfo]);

  if (!divisionId) {
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-1">Invalid Season</h1>
          <p className="text-slate-500 mb-4">No division ID provided.</p>
          <Link to="/" className="text-brand-600 hover:text-brand-700 font-medium text-sm">← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <LoadingSpinner size="lg" message="Loading season rankings..." />
      </div>
    );
  }

  if (error) {
    const isNotCached = error.message?.includes('No cached rankings') || error.message?.includes('404');
    const isTimeout = error.message?.includes('timed out') || error.message?.includes('504');
    const isDataUnavailable = isNotCached || isTimeout;
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <div className="text-center">
          <AlertTriangle className={`w-10 h-10 mx-auto mb-3 ${isDataUnavailable ? 'text-amber-400' : 'text-red-400'}`} />
          <h1 className="text-xl font-bold text-slate-900 mb-1">
            {isDataUnavailable ? 'Rankings Not Available' : 'Error Loading Data'}
          </h1>
          <p className="text-slate-500 mb-4">
            {isDataUnavailable
              ? 'Rankings for this season haven\'t been fetched yet.'
              : error.message}
          </p>
          <div className="flex gap-3 justify-center">
            {canRefresh && isDataUnavailable && (
              <button
                onClick={() => refreshMutation.mutate()}
                disabled={isRefreshing}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {isRefreshing ? 'Fetching Data…' : 'Fetch Rankings'}
              </button>
            )}
            {!isDataUnavailable && (
              <button onClick={() => window.location.reload()} className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors">
                Retry
              </button>
            )}
            <Link to="/" className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
              Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!rankings) return null;

  return (
    <div className="flex-1 min-h-0 flex flex-col max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      {/* Breadcrumb + Title */}
      <div className="mb-5 flex-shrink-0">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600 transition-colors mb-2">
          <ChevronLeft className="w-3.5 h-3.5" />
          Dashboard
        </Link>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">
            {seasonName}
          </h1>
          <span className="flex items-center gap-1.5 text-sm text-slate-400">
            <Users className="w-4 h-4" />
            {rankings.total_players} players
          </span>
          {showMatchup && (
            <Link
              to={`/division/${divisionId}/matchup`}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-500 hover:text-blue-600 hover:bg-blue-50 active:bg-blue-100 rounded-lg transition-colors"
              title="Matchup optimizer"
            >
              ⚔️ Matchups
            </Link>
          )}
          {isWriteEnabled && (
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={isRefreshing || !canRefresh}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 hover:bg-brand-50 active:bg-brand-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh all player data"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          )}
        </div>
        {/* Season info bar */}
        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
          {seasonInfo?.division_ids && (
            <span className="flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" />
              Divisions: {seasonInfo.division_ids.map(id => `#${id}`).join(', ')}
            </span>
          )}
          {dateRange && (
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {dateRange}
            </span>
          )}
          {seasonInfo && (
            <span>{seasonInfo.match_count} matches</span>
          )}
        </div>
      </div>

      {/* View toggle: Players | Schedule */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg mb-4 w-fit flex-shrink-0">
        <button
          onClick={() => setViewMode('players')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            viewMode === 'players'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700 hover:bg-white/50 active:bg-white/70'
          }`}
        >
          <Table2 className="w-4 h-4" />
          Players
        </button>
        <button
          onClick={() => setViewMode('schedule')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            viewMode === 'schedule'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700 hover:bg-white/50 active:bg-white/70'
          }`}
        >
          <CalendarDays className="w-4 h-4" />
          Schedule
        </button>
      </div>

      {viewMode === 'players' ? (
        <>
          {/* Controls */}
          <div className="mb-4 flex-shrink-0">
            <ControlPanel
              filters={filters}
              onFiltersChange={setFilters}
              activeGameTab={activeGameTab}
              onGameTabChange={(tab) => setActiveGameTab(tab as GameTabKey)}
              availableGameTabs={availableGameTabs}
            />
          </div>

          {/* Table — scrolls independently while header above stays fixed */}
          <div className="flex-1 min-h-0 flex flex-col pb-6">
            <PlayerTable
              players={filteredPlayers}
              filters={filters}
              onFiltersChange={setFilters}
              activeGameTab={activeGameTab}
              periodStats={isDivisionCurrent ? undefined : periodStats?.player_stats}
            />
          </div>
        </>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto pb-6">
          <ScheduleView
            divisionIds={seasonInfo?.division_ids ?? [divisionId!]}
            divisions={seasonInfo?.divisions ?? []}
            players={rankings?.players}
          />
        </div>
      )}
    </div>
  );
}
